import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { useBulkImportChannels } from "@/lib/channels";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";

/**
 * BulkChannelDialog — paste-many import (DESIGN-SYSTEM Dialog). Invalid rows are
 * surfaced inline as an Alert list (IA §3: errors are important, not hidden in a
 * nested <details>).
 */
export function BulkChannelDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const bulk = useBulkImportChannels();
  const [text, setText] = useState("");
  const [result, setResult] =
    useState<Awaited<ReturnType<typeof bulk.mutateAsync>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const r = await bulk.mutateAsync({ text, format: "username" });
      setResult(r);
      if (r.invalid_lines.length === 0) setText("");
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("channels.bulk_error"));
    }
  };

  const reset = () => {
    setText("");
    setResult(null);
    setError(null);
  };

  const lineCount = text
    .split("\n")
    .filter((l) => l.trim() && !l.trim().startsWith("#")).length;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent size="lg">
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>{t("channels.bulk.title")}</DialogTitle>
            <DialogDescription className="whitespace-pre-line">
              {t("channels.bulk.hint")}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="bulk_text">
                {t("channels.bulk.label")}
                <span className="ml-2 font-normal text-fg-muted">
                  ({t("channels.bulk.lines_count", { count: lineCount })})
                </span>
              </Label>
              <textarea
                id="bulk_text"
                required
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={
                  "Группы / чаты\n\n" +
                  "https://t.me/durov_lab чат\n" +
                  "https://t.me/telegram\n" +
                  "https://t.me/+AbCdEf123456 чат !!\n" +
                  "@some_channel\n" +
                  "-1001234567890"
                }
                className="flex w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-sm text-fg-primary placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
            </div>

            {result && (
              <Alert
                tone={result.invalid_lines.length > 0 ? "warning" : "success"}
                title={t("channels.bulk.result_title")}
              >
                <ul className="space-y-1">
                  <li>
                    <span className="font-medium text-success">
                      {result.inserted}
                    </span>{" "}
                    {t("channels.bulk.inserted")}
                  </li>
                  <li>
                    <span className="font-medium text-fg-muted">
                      {result.skipped_duplicates}
                    </span>{" "}
                    {t("channels.bulk.skipped")}
                  </li>
                  {result.skipped_noise > 0 && (
                    <li>
                      <span className="font-medium text-fg-muted">
                        {result.skipped_noise}
                      </span>{" "}
                      {t("channels.bulk.skipped_noise")}
                    </li>
                  )}
                  {result.invalid_lines.length > 0 && (
                    <li>
                      <span className="font-medium text-danger">
                        {result.invalid_lines.length}
                      </span>{" "}
                      {t("channels.bulk.invalid")}
                    </li>
                  )}
                </ul>
              </Alert>
            )}

            {result && result.invalid_lines.length > 0 && (
              <Alert tone="danger" title={t("channels.bulk.show_invalid")}>
                <ul className="space-y-1 font-mono text-xs">
                  {result.invalid_lines.map((l) => (
                    <li key={l.line_number}>
                      [{l.line_number}] {l.raw} — {l.reason}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {error && <Alert tone="danger">{error}</Alert>}
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {t("common.close")}
              </Button>
            </DialogClose>
            <Button type="submit" loading={bulk.isPending} disabled={!text.trim()}>
              {t("channels.bulk.run")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
